import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import ProtectedRoute from '../components/protectedRoute/ProtectedRoute';
import LoadingSpinner from '../components/loading/LoadingSpinner';

// Lazy load pages
const AgencyManagement = lazy(() => import('../pages/agencyManagement/AgencyManagement'));
const AgencyUser = lazy(() => import('../pages/agencyUser/AgencyUser'));
const EventManagementPage = lazy(() => import('../pages/eventManagement/EventManagementPage'));
const LandingPage = lazy(() => import('../pages/landing/LandingPage'));
const Login = lazy(() => import('../pages/login/Login'));
const PrivacyPolicy = lazy(() => import('../pages/legal/PrivacyPolicy'));
const TermsConditions = lazy(() => import('../pages/legal/TermsConditions'));
const CookiePolicy = lazy(() => import('../pages/legal/CookiePolicy'));
const ContactUs = lazy(() => import('../pages/legal/ContactUs'));
const DashboardAgency = lazy(() => import('../pages/dashboard/DashboardAgency'));
const BeneficiaryManagement = lazy(() => import('../pages/beneficiaryManagement/BeneficiaryManagement'));
const CreateAgency = lazy(() => import('../pages/createAgency/createAgency'));
const TaskManagement = lazy(() => import('../pages/taskManagement/TaskManagement'));
const TaskDetails = lazy(() => import('../pages/taskDetails/TaskDetails'));
const TeamDetails = lazy(() => import('../pages/teamDetails/TeamDetails'));
const TeamDetailslist = lazy(() => import('../pages/teamDetails/TeamDetailsList'));
const TeamUserManagement = lazy(() => import('../pages/teamUserManagement/TeamUserManagement'));
const AccountingManagement = lazy(() => import('../pages/accountingManagement/AccountingManagement'));
const TaskManager = lazy(() => import('../pages/taskManager/TaskManager'));
const Agencies = lazy(() => import('../pages/agencies/Agencies'));
const UserManagement = lazy(() => import('../pages/userManagement/UserManagement'));
const CreateUser = lazy(() => import('../pages/createUser/CreateUser'));
const Npos = lazy(() => import('../pages/npos/Npos'));
const CreateNpo = lazy(() => import('../pages/createNpo/CreateNpo'));
const ModifyAgency = lazy(() => import('../pages/createAgency/modifyAgency'));
const ModifyNpo = lazy(() => import('../pages/createNpo/ModifyNpo'));
const UpdateUser = lazy(() => import('../pages/agencyUser/UpdateUser'));
const AgencyNpos = lazy(() => import('../pages/npos/AgencyNpos'));
const ChangePassword = lazy(() => import('../pages/agencyUser/ChangePassword'));
const CreateTeam = lazy(() => import('../pages/createTeam/CreateTeam'));
const ModifyTeam = lazy(() => import('../pages/teamDetails/ModifyTeam'));
const CreateEvent = lazy(() => import('../pages/eventManagement/CreateEvent'));
const ModifyEvent = lazy(() => import('../pages/eventManagement/ModifyEvent'));
const EventTaskDashboard = lazy(() => import('../pages/eventManagement/EventTaskDashboard'));
const DuplicateEvent = lazy(() => import('../pages/eventManagement/DuplicateEvent'));
const CreateSlots = lazy(() => import('../pages/Slots/CreateSlots'));
const SlotsData = lazy(() => import('../pages/Slots/SlotsData'));
const ModifySlot = lazy(() => import('../pages/Slots/ModifySlot'));
const SignUp = lazy(() => import('../pages/signUp/SignUp'));
const SpecificEvent = lazy(() => import('../pages/eventManagement/SpecificEvent'));
const UpdateTimeRequest = lazy(() => import('../pages/eventManagement/UpdateTimeRequest'));
const UpdateRequests = lazy(() => import('../pages/eventManagement/UpdateRequests'));
const MediatorUpdateRequests = lazy(() => import('../pages/eventManagement/MediatorUpdateRequests'));
const EditProfile = lazy(() => import('../pages/profile/EditProfile'));
const Locations = lazy(() => import('../pages/locations/Locations'));
const CreateLocation = lazy(() => import('../pages/locations/CreateLocation'));
const EditLocation = lazy(() => import('../pages/locations/EditLocation'));
const Tasks = lazy(() => import('../pages/tasks/Tasks'));
const CreateTask = lazy(() => import('../pages/tasks/CreateTask'));
const SharedTasksList = lazy(() => import('../pages/sharedTasks/SharedTasksList'));
const SharedTaskDetail = lazy(() => import('../pages/sharedTasks/SharedTaskDetail'));
const EditTask = lazy(() => import('../pages/tasks/EditTask'));
const MediatorRequests = lazy(() => import('../pages/mediatorRequests/MediatorRequests'));
const CreateMediatorRequest = lazy(() => import('../pages/mediatorRequests/CreateMediatorRequest'));
const MediatorPendingRequests = lazy(() => import('../pages/mediatorRequests/MediatorPendingRequests'));
const Reports = lazy(() => import('../pages/Reports/Reports'));
const AgreementList = lazy(() => import('../pages/agreements/AgreementList'));
const InvoiceManagement = lazy(() => import('../pages/invoices/InvoiceManagement'));
const CreateInvoice = lazy(() => import('../pages/invoices/CreateInvoice'));
const HierarchyManagement = lazy(() => import('../pages/hierarchyManagement/HierarchyManagement'));
const PasswordSetup = lazy(() => import('../pages/passwordSetup/PasswordSetup'));
const ForgotPassword = lazy(() => import('../pages/forgotPassword/ForgotPassword'));
const ImportData = lazy(() => import('../pages/importData/ImportData'));
const MediatorAgreements = lazy(() => import('../pages/mediatorAgreements/MediatorAgreements'));
const CreateMediatorAgreement = lazy(() => import('../pages/mediatorAgreements/CreateMediatorAgreement'));
const ProjectAgreements = lazy(() => import('../pages/projectAgreements/ProjectAgreements'));
const MediatorAgreementsView = lazy(() => import('../pages/mediatorAgreements/MediatorAgreementsView'));
const PlansManagement = lazy(() => import('../pages/plansManagement/PlansManagement'));
const AdminPanel = lazy(() => import('../pages/adminPanel/AdminPanel'));
const VideosManagement = lazy(() => import('../pages/videosManagement/VideosManagement'));
const Tutorials = lazy(() => import('../pages/tutorials/Tutorials'));
const SupportManager = lazy(() => import('../pages/supportManager/SupportManager'));
const SupportTicketDetail = lazy(() => import('../pages/supportManager/SupportTicketDetail'));
const FreelanceArea = lazy(() => import('../pages/freelanceArea/FreelanceArea'));
const PublicSignPage = lazy(() => import('../pages/public/PublicSignPage'));


const AppRoutes = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public Landing Route */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/public/sign/:token" element={<PublicSignPage />} />
        
        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/set-password" element={<PasswordSetup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardAgency />
          </ProtectedRoute>
        } />
        <Route path="/users-management" element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        } />
        <Route path="/plans-management" element={
          <ProtectedRoute>
            <PlansManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin-panel" element={
          <ProtectedRoute>
            <AdminPanel />
          </ProtectedRoute>
        } />
        <Route path="/tutorials" element={
          <ProtectedRoute>
            <Tutorials />
          </ProtectedRoute>
        } />
        <Route path="/videos-management" element={
          <ProtectedRoute>
            <VideosManagement />
          </ProtectedRoute>
        } />
        <Route path="/support-manager" element={
          <ProtectedRoute>
            <SupportManager />
          </ProtectedRoute>
        } />
        <Route path="/support-manager/:id" element={
          <ProtectedRoute>
            <SupportTicketDetail />
          </ProtectedRoute>
        } />
        <Route path="/agency-management" element={
          <ProtectedRoute>
            <AgencyManagement />
          </ProtectedRoute>
        } />
        <Route path="/agencies" element={
          <ProtectedRoute>
            <Agencies />
          </ProtectedRoute>
        } />
        <Route path="/npos" element={
          <ProtectedRoute>
            <Npos />
          </ProtectedRoute>
        } />
        <Route path="/modify-agency/:id" element={
          <ProtectedRoute>
            <ModifyAgency />
          </ProtectedRoute>
        } />
        <Route path="/modify-npo/:id" element={
          <ProtectedRoute>
            <ModifyNpo />
          </ProtectedRoute>
        } />
        <Route path="/agency-npos/:id" element={
          <ProtectedRoute>
            <AgencyNpos />
          </ProtectedRoute>
        } />
        <Route path="/create-team" element={
          <ProtectedRoute>
            <CreateTeam />
          </ProtectedRoute>
        } />
        <Route path="/modify-team/:id" element={
          <ProtectedRoute>
            <ModifyTeam />
          </ProtectedRoute>
        } />
        <Route path="/modify-event/:id" element={
          <ProtectedRoute>
            <ModifyEvent />
          </ProtectedRoute>
        } />
        <Route path="/duplicate-event/:eventId" element={
          <ProtectedRoute>
            <DuplicateEvent />
          </ProtectedRoute>
        } />
        <Route path="/modify-slot/:id" element={
          <ProtectedRoute>
            <ModifySlot />
          </ProtectedRoute>
        } />
        <Route path="/specific-event/:id" element={
          <ProtectedRoute>
            <SpecificEvent />
          </ProtectedRoute>
        } />
        <Route path="/agreements/:id" element={
          <ProtectedRoute>
            <AgreementList />
          </ProtectedRoute>
        } />
        <Route path="/mediator-agreements" element={
          <ProtectedRoute>
            <MediatorAgreements />
          </ProtectedRoute>
        } />
        <Route path="/create-mediator-agreement" element={
          <ProtectedRoute>
            <CreateMediatorAgreement />
          </ProtectedRoute>
        } />
        <Route path="/project-agreements/:projectId" element={
          <ProtectedRoute>
            <ProjectAgreements />
          </ProtectedRoute>
        } />
        <Route path="/my-agreements" element={
          <ProtectedRoute>
            <MediatorAgreementsView />
          </ProtectedRoute>
        } />

        <Route path="/edit-profile/:id" element={
          <ProtectedRoute>
            <EditProfile />
          </ProtectedRoute>
        } />

        <Route path="/agency-user" element={
          <ProtectedRoute>
            <AgencyUser />
          </ProtectedRoute>
        } />
        <Route path="/update-requests" element={
          <ProtectedRoute>
            <UpdateRequests />
          </ProtectedRoute>
        } />
        <Route path="/mediator-update-requests" element={
          <ProtectedRoute>
            <MediatorUpdateRequests />
          </ProtectedRoute>
        } />
        <Route path="/update-time-request/:id" element={
          <ProtectedRoute>
            <UpdateTimeRequest />
          </ProtectedRoute>
        } />
        <Route path="/slots-data" element={
          <ProtectedRoute>
            <SlotsData />
          </ProtectedRoute>
        } />
        <Route path="/create-slots" element={
          <ProtectedRoute>
            <CreateSlots />
          </ProtectedRoute>
        } />
        <Route path="/create-event" element={
          <ProtectedRoute>
            <CreateEvent />
          </ProtectedRoute>
        } />
        <Route path="/create-event/:id" element={
          <ProtectedRoute>
            <CreateEvent />
          </ProtectedRoute>
        } />
        <Route path="/change-password" element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        } />
        <Route path="/update-user" element={
          <ProtectedRoute>
            <UpdateUser />
          </ProtectedRoute>
        } />
        <Route path="/update-user/:id" element={
          <ProtectedRoute>
            <UpdateUser />
          </ProtectedRoute>
        } />
        <Route path="/freelance" element={
          <ProtectedRoute>
            <FreelanceArea />
          </ProtectedRoute>
        } />
        <Route path="/freelance/:id" element={
          <ProtectedRoute>
            <FreelanceArea />
          </ProtectedRoute>
        } />
        <Route path="/event-management/:id" element={
          <ProtectedRoute>
            <EventManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/event-tasks" element={
          <ProtectedRoute>
            <EventTaskDashboard />
          </ProtectedRoute>
        } />
        <Route path="/event-management" element={
          <ProtectedRoute>
            <EventManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/beneficiary-events" element={
          <ProtectedRoute>
            <EventManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/user-management" element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        } />
        <Route path="/beneficiary-management" element={
          <ProtectedRoute>
            <BeneficiaryManagement />
          </ProtectedRoute>
        } />
        <Route path="/create-agency" element={
          <ProtectedRoute>
            <CreateAgency />
          </ProtectedRoute>
        } />
        <Route path="/create-npo" element={
          <ProtectedRoute>
            <CreateNpo />
          </ProtectedRoute>
        } />
        <Route path="/create-npo/:id" element={
          <ProtectedRoute>
            <CreateNpo />
          </ProtectedRoute>
        } />
        <Route path="/create-user" element={
          <ProtectedRoute>
            <CreateUser />
          </ProtectedRoute>
        } />
        <Route path="/task-management" element={
          <ProtectedRoute>
            <TaskManagement />
          </ProtectedRoute>
        } />
        <Route path="/task-details" element={
          <ProtectedRoute>
            <TaskDetails />
          </ProtectedRoute>
        } />
        <Route path="/tasks/:taskId/teams" element={
          <ProtectedRoute>
            <TeamDetailslist />
          </ProtectedRoute>
        } />
        <Route path="/team-user-management" element={
          <ProtectedRoute>
            <TeamUserManagement />
          </ProtectedRoute>
        } />
        <Route path="/accounting-management" element={
          <ProtectedRoute>
            <AccountingManagement />
          </ProtectedRoute>
        } />
        <Route path="/task-manager" element={
          <ProtectedRoute>
            <TaskManager />
          </ProtectedRoute>
        } />
        <Route path="/locations" element={
          <ProtectedRoute>
            <Locations />
          </ProtectedRoute>
        } />
        <Route path="/create-location" element={
          <ProtectedRoute>
            <CreateLocation />
          </ProtectedRoute>
        } />
        <Route path="/edit-location/:id" element={
          <ProtectedRoute>
            <EditLocation />
          </ProtectedRoute>
        } />
        <Route path="/tasks" element={
          <ProtectedRoute>
            <Tasks />
          </ProtectedRoute>
        } />
        <Route path="/create-task" element={
          <ProtectedRoute>
            <CreateTask />
          </ProtectedRoute>
        } />
        <Route path="/edit-task/:id" element={
          <ProtectedRoute>
            <EditTask />
          </ProtectedRoute>
        } />
        <Route path="/shared-tasks" element={
          <ProtectedRoute>
            <SharedTasksList />
          </ProtectedRoute>
        } />
        <Route path="/shared-tasks/:projectId" element={
          <ProtectedRoute>
            <SharedTaskDetail />
          </ProtectedRoute>
        } />
        <Route path="/import-data" element={
          <ProtectedRoute>
            <ImportData />
          </ProtectedRoute>
        } />
        <Route path="/mediator-requests" element={
          <ProtectedRoute>
            <MediatorRequests />
          </ProtectedRoute>
        } />
        <Route path="/create-mediator-request" element={
          <ProtectedRoute>
            <CreateMediatorRequest />
          </ProtectedRoute>
        } />
        <Route path="/mediator-pending-requests" element={
          <ProtectedRoute>
            <MediatorPendingRequests />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/invoices" element={
          <ProtectedRoute>
            <InvoiceManagement />
          </ProtectedRoute>
        } />
        <Route path="/create-invoice" element={
          <ProtectedRoute>
            <CreateInvoice />
          </ProtectedRoute>
        } />
        <Route path="/hierarchy-management" element={
          <ProtectedRoute>
            <HierarchyManagement />
          </ProtectedRoute>
        } />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
